import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layout, Tree, Input, Spin, Empty, Tag, Typography, Space, Button, Tabs,
  Card, Table, message, Alert, Statistic, Row, Col, Tooltip, Segmented,
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, DatabaseOutlined, TableOutlined,
  KeyOutlined, LinkOutlined, FieldNumberOutlined,
  ApartmentOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router';
import {
  getVaultErd, listConnections, listDagitRuns, getDagitRun,
  getVaultTableSchema, getVaultData,
} from '../services/dataPlatformService';
import {
  buildSyncHistoryRows, filterSyncRunsForStream, resolveStreamAndPrefix,
  syncHistoryStatusColor,
} from '../services/syncHistory';
import ResetVaultDialog from '../components/ResetVaultDialog';
import { useVaultData } from '../contexts/VaultDataContext';

const THEME_CARD_BG = 'var(--theme-cardBg, #fff)';
const JUSTIFY_SPACE_BETWEEN = 'space-between';

const { Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;

// ── Pure helpers extracted from DataExplorerPage ──────────────────────
// These collapse the many `isEmbedded ? a : b` branches that previously
// lived directly in the component body (each one adds to that function's
// cyclomatic/cognitive complexity) into a handful of small, independently
// testable functions. No behavior change — same inputs produce the same
// outputs as the inline ternaries they replace.

// Embedded (inside VaultDataContext) vs standalone (URL-param-driven)
// picks one value or the other; used once per field below so no single
// function accumulates all 6 branches.
function pickEmbedded(isEmbedded, embeddedVal, localVal) {
  return isEmbedded ? embeddedVal : localVal;
}

export function resolveExplorerViewState({
  isEmbedded, vault, erdLocal, loadingLocal, paramsLocal, activeTabLocal,
}) {
  // `?.` because every embedded-branch argument below is evaluated
  // eagerly (pickEmbedded is a plain function call, not a ternary) —
  // `vault` is null in the standalone tests even when isEmbedded is
  // false, so a bare `vault.x` here would throw before pickEmbedded
  // ever got to pick the (correct) local value instead.
  const erd = pickEmbedded(
    isEmbedded, { tables: vault?.tables, relationships: vault?.relationships }, erdLocal,
  );
  const loading = pickEmbedded(isEmbedded, vault?.loading, loadingLocal);
  const selectedSchema = pickEmbedded(isEmbedded, vault?.selectedSchema, paramsLocal.get('schema') || '');
  const selectedTable = pickEmbedded(isEmbedded, vault?.selectedTable, paramsLocal.get('table') || '');
  const layer = pickEmbedded(isEmbedded, vault?.layer, paramsLocal.get('layer') || 'bronze');
  const activeTab = pickEmbedded(isEmbedded, activeTabLocal, paramsLocal.get('tab') || 'details');
  return { erd, loading, selectedSchema, selectedTable, layer, activeTab };
}

export function resolveEmbeddedSchemaInfo({ isEmbedded, vault, layer }) {
  const embeddedSchemaName = isEmbedded ? (vault.selectedSchemaName || '') : '';
  const embeddedSchemaLayer = isEmbedded ? (vault.selectedSchemaLayer || layer) : layer;
  return { embeddedSchemaName, embeddedSchemaLayer };
}

export function computeIsEmpty({ isEmbedded, vault, erd }) {
  return isEmbedded
    ? (vault?.layerSchemas?.length || 0) === 0
    : (!erd || erd.tables.length === 0);
}

export function resolveRightPaneKind({
  isEmbedded, selectedTable, embeddedSchemaLoading, embeddedTableSchema, selected,
}) {
  const embeddedPending = isEmbedded && selectedTable && embeddedSchemaLoading && !embeddedTableSchema;
  if (embeddedPending) {return 'pending';}
  if (!selected) {return 'empty';}
  if (isEmbedded) {return 'embedded';}
  return 'standalone';
}

export function buildLayerSegmentedOptions(layerCounts) {
  return [
    { value: 'staging', label: `Staging (${layerCounts.get('staging') || 0})`, disabled: !layerCounts.get('staging') },
    { value: 'bronze', label: `Bronze (${layerCounts.get('bronze') || 0})`, disabled: !layerCounts.get('bronze') },
    { value: 'silver', label: `Silver (${layerCounts.get('silver') || 0})`, disabled: !layerCounts.get('silver') },
    { value: 'gold', label: `Gold (${layerCounts.get('gold') || 0})`, disabled: !layerCounts.get('gold') },
    ...(layerCounts.get('legacy') ? [{ value: 'legacy', label: `Legacy (${layerCounts.get('legacy')})` }] : []),
  ];
}

// Renderers keyed by `resolveRightPaneKind`'s result — a lookup instead of
// an if/else-if chain in the component body. Defined at module scope so it
// isn't recreated every render; the referenced components are `function`
// declarations below and are only invoked (not read) at module-eval time,
// so hoisting order doesn't matter.
const RIGHT_PANE_RENDERERS = {
  pending: () => (
    <div style={{ padding: 64, textAlign: 'center' }}>
      <Spin />
    </div>
  ),
  empty: () => <EmptyRight />,
  embedded: ({ selected }) => <TableOverviewPanel table={selected} />,
  standalone: ({ selected, connections, activeTab, onTab }) => (
    <TableDetailPanes table={selected} connections={connections} activeTab={activeTab} onTab={onTab} />
  ),
};

/**
 * DataExplorerPage — Snowflake-style two-column data explorer.
 *
 * Layout:
 *   Left (Sider, ~320px): tree of schemas → tables, with search.
 *   Right: tabs — Table Details · Columns · Sync History.
 *
 * URL state: ?schema=<connector>&table=<name>&tab=<details|columns|history>
 *
 * When rendered inside the DATA CENTER tabs (`embedded` prop), the page
 * skips its own data fetch and left sidebar and reads selection state
 * from `VaultDataContext` instead. The standalone path (no `embedded`)
 * keeps working for any legacy routes.
 */
export default function DataExplorerPage({ embedded = false }) {
  const navigate = useNavigate();
  const vault = useVaultData();
  const isEmbedded = embedded && !!vault;

  // Standalone state (only used when not embedded).
  const [erdLocal, setErdLocal] = useState(null);
  const [loadingLocal, setLoadingLocal] = useState(true);
  const [search, setSearch] = useState('');
  const [paramsLocal, setParamsLocal] = useSearchParams();

  // Connections are still fetched here (used by Sync History tab) — both
  // standalone and embedded callers need them.
  const [connections, setConnections] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);

  // Resolve the data source: shared context when embedded, local fetch
  // otherwise. The `loading` / `erd` reads below pick up whichever is in
  // play without further branching.
  const [activeTabLocal, setActiveTabLocal] = useState('details');
  const {
    erd, loading, selectedSchema, selectedTable, layer, activeTab,
  } = resolveExplorerViewState({
    isEmbedded, vault, erdLocal, loadingLocal, paramsLocal, activeTabLocal,
  });
  const setActiveTab = (k) => {
    if (isEmbedded) {setActiveTabLocal(k);}
    else {setParamsLocal({ schema: selectedSchema, table: selectedTable, tab: k, layer });}
  };
  // setParams shim — only used in standalone mode.
  const params = paramsLocal;
  const setParams = setParamsLocal;

  const refresh = useCallback(async () => {
    // Embedded mode delegates the vault fetch to the shared context.
    // Only the connections list is page-local.
    if (isEmbedded) {
      try {
        const c = await listConnections();
        setConnections(c || []);
      } catch (err) {
        // Connections are optional (Sync History tab) — don't block.
      }
      return;
    }
    setLoadingLocal(true);
    try {
      const [e, c] = await Promise.all([
        getVaultErd(),
        listConnections().catch(() => []),
      ]);
      setErdLocal(e);
      setConnections(c);
    } catch (err) {
      message.warning(`Couldn't load vault: ${err.message}`);
      setErdLocal({ tables: [], relationships: [] });
    } finally {
      setLoadingLocal(false);
    }
  }, [isEmbedded]);
  useEffect(() => { refresh(); }, [refresh]);

  // Layer counts (for the segmented switcher).
  const layerCounts = useMemo(() => {
    const m = new Map();
    for (const t of erd?.tables || []) {
      const k = t.layer || 'legacy';
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [erd]);

  // Auto-pick a non-empty layer when the current one has no tables.
  // (Embedded mode delegates this to VaultDataContext.)
  useEffect(() => {
    if (isEmbedded || !erd) {return;}
    if (!layerCounts.get(layer)) {
      const order = ['bronze', 'staging', 'silver', 'gold', 'legacy'];
      const fallback = order.find((l) => layerCounts.get(l));
      if (fallback && fallback !== layer) {
        const next = new URLSearchParams(params);
        next.set('layer', fallback);
        setParams(next, { replace: true });
      }
    }
  }, [isEmbedded, erd, layerCounts, layer, params, setParams]);

  // Group tables by schema (= connector) within the selected layer.
  // Snowflake-style: schemas are top level and tables are nested children.
  const grouped = useMemo(() => {
    if (!erd) {return [];}
    const m = new Map();
    for (const t of erd.tables) {
      if ((t.layer || 'legacy') !== layer) {continue;}
      const key = t.connector;
      if (!m.has(key)) {m.set(key, []);}
      m.get(key).push(t);
    }
    return [...m.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([connector, tables]) => ({
        connector,
        tables: tables.sort((a, b) => a.table.localeCompare(b.table)),
      }));
  }, [erd, layer]);

  const filteredGrouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {return grouped;}
    return grouped
      .map(({ connector, tables }) => ({
        connector,
        tables: tables.filter((t) =>
          t.table.toLowerCase().includes(q) || connector.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.tables.length > 0);
  }, [grouped, search]);

  const treeData = useMemo(
    () => filteredGrouped.map(({ connector, tables }) => ({
      key: `s::${connector}`,
      title: (
        <Space size={6}>
          <DatabaseOutlined style={{ color: '#722ed1' }} />
          <Text strong>{connector}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>({tables.length})</Text>
        </Space>
      ),
      selectable: false,
      children: tables.map((t) => ({
        key: `t::${connector}::${t.table}`,
        title: (
          <Space size={6}>
            <TableOutlined style={{ color: '#1677ff' }} />
            <Text>{t.table}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {(t.row_count || 0).toLocaleString()}
            </Text>
          </Space>
        ),
        isLeaf: true,
      })),
    })),
    [filteredGrouped],
  );

  const expandedKeys = useMemo(
    () => filteredGrouped.map((g) => `s::${g.connector}`),
    [filteredGrouped],
  );

  const selectedKey = selectedTable ? [`t::${selectedSchema}::${selectedTable}`] : [];

  const onSelectTable = (keys) => {
    const k = keys[0];
    if (!k || !k.startsWith('t::')) {return;}
    const [, schema, table] = k.split('::');
    setParams({ schema, table, tab: activeTab || 'details', layer });
  };

  // Standalone mode: resolve the selected table from the full ERD payload
  // (which carries PK/FK metadata, row counts, etc).
  const selectedFromErd = useMemo(() => {
    if (!erd || !selectedTable) {return null;}
    return erd.tables.find(
      (t) => t.connector === selectedSchema
        && t.table === selectedTable
        && (t.layer || 'legacy') === layer,
    ) || null;
  }, [erd, selectedSchema, selectedTable, layer]);

  // Embedded mode: per-table fetch via /vault/metadata?kind=columns so opening the
  // Catalog tab and clicking a table doesn't force a full ERD load (which
  // would also pull every other schema's columns + FK graph). The
  // lightweight schema_name comes from `vault.layerTables`, which the
  // sidebar populates lazily as the user expands schemas.
  const [embeddedTableSchema, setEmbeddedTableSchema] = useState(null);
  const [embeddedSchemaLoading, setEmbeddedSchemaLoading] = useState(false);

  // Physical schema for the selection — resolved centrally in
  // VaultDataContext so a table in any shown layer (not just layers[0])
  // maps to its real `<layer>__<connector>` schema.
  const { embeddedSchemaName, embeddedSchemaLayer } = resolveEmbeddedSchemaInfo({
    isEmbedded, vault, layer,
  });

  useEffect(() => {
    if (!isEmbedded) {return undefined;}
    if (!embeddedSchemaName || !selectedTable) {
      setEmbeddedTableSchema(null);
      return undefined;
    }
    let cancelled = false;
    setEmbeddedSchemaLoading(true);
    getVaultTableSchema(embeddedSchemaName, selectedTable)
      .then((res) => {
        if (cancelled) {return;}
        setEmbeddedTableSchema(res);
      })
      .catch((err) => {
        if (cancelled) {return;}
        setEmbeddedTableSchema(null);
        message.warning(`Couldn't load table schema: ${err.message}`);
      })
      .finally(() => {
        if (!cancelled) {setEmbeddedSchemaLoading(false);}
      });
    return () => { cancelled = true; };
  }, [isEmbedded, embeddedSchemaName, selectedTable]);

  // Shape the embedded selection into the same `selected` envelope used
  // by the standalone right pane — columns from the per-table fetch,
  // identity from the cached layerTables row (so no ERD round-trip).
  const selectedEmbedded = useMemo(() => {
    if (!isEmbedded || !selectedTable) {return null;}
    const columns = embeddedTableSchema?.columns || [];
    return {
      schema_name: embeddedSchemaName,
      table: selectedTable,
      connector: selectedSchema,
      layer: embeddedSchemaLayer,
      columns,
    };
  }, [isEmbedded, selectedTable, embeddedTableSchema, embeddedSchemaName, selectedSchema, embeddedSchemaLayer]);

  const selected = isEmbedded ? selectedEmbedded : selectedFromErd;

  if (loading) {return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;}
  // In embedded mode the sidebar's schemas list is the canonical "do we
  // have data?" signal — ERD may still be loading lazily. Standalone
  // mode keeps the original ERD-based check.
  const isEmpty = computeIsEmpty({ isEmbedded, vault, erd });

  if (isEmpty) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={3}>Data Explorer</Title>
        <Card>
          <Empty description={
            <Space orientation="vertical" align="center">
              <Text>No data in your vault yet.</Text>
              <Text type="secondary">Connect a source and run a sync to start exploring.</Text>
            </Space>
          }>
            <Button type="primary" onClick={() => navigate('/pipeline/sources')}>Browse sources</Button>
          </Empty>
        </Card>
      </div>
    );
  }

  // Right-pane content — used by both standalone and embedded layouts.
  // Embedded mode shows the merged Details+Columns view (Sync History is
  // a top-level tab in Data Center now); standalone keeps the legacy
  // three-tab layout.
  const rightPaneKind = resolveRightPaneKind({
    isEmbedded, selectedTable, embeddedSchemaLoading, embeddedTableSchema, selected,
  });
  const rightPane = RIGHT_PANE_RENDERERS[rightPaneKind]({
    selected, connections, activeTab, onTab: setActiveTab,
  });

  // Embedded layout: no Sider — the shared <VaultSidebar> is owned by
  // DataCenter. Just render the right pane full-width.
  if (isEmbedded) {
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: 24, background: THEME_CARD_BG }}>
        {rightPane}
      </div>
    );
  }

  return (
    <Layout style={{ height: '100%', background: THEME_CARD_BG }}>
      <Sider
        width={380}
        theme="light"
        style={{
          borderRight: '1px solid var(--theme-border, #f0f0f0)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: THEME_CARD_BG,
        }}
      >
        <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0' }}>
          <Space style={{ width: '100%', justifyContent: JUSTIFY_SPACE_BETWEEN, marginBottom: 8 }}>
            <Text strong>Schemas</Text>
            <Space size={4}>
              <Tooltip title="Reset data…">
                <Button
                  size="small"
                  danger
                  onClick={() => setResetOpen(true)}
                >
                  Reset…
                </Button>
              </Tooltip>
              <Tooltip title="Refresh">
                <Button size="small" icon={<ReloadOutlined />} onClick={refresh} />
              </Tooltip>
            </Space>
          </Space>
          <Segmented
            block
            size="small"
            value={layer}
            options={buildLayerSegmentedOptions(layerCounts)}
            onChange={(v) => {
              // Switching layer clears the table selection because the same
              // table name may not exist in both layers.
              const next = new URLSearchParams();
              next.set('layer', v);
              setParams(next);
            }}
            style={{ marginBottom: 8 }}
          />
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            placeholder="Search tables…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ flex: 1, overflow: 'auto', height: 'calc(100% - 115px)', padding: 8 }}>
          {treeData.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matches" />
          ) : (
            <Tree
              blockNode
              showLine={{ showLeafIcon: false }}
              treeData={treeData}
              expandedKeys={expandedKeys}
              selectedKeys={selectedKey}
              onSelect={onSelectTable}
              autoExpandParent
            />
          )}
        </div>
      </Sider>
      <Content style={{ overflow: 'auto', padding: 24, background: THEME_CARD_BG }}>
        {/* `rightPane` (computed above via resolveRightPaneKind) is already
            the correct standalone view here: isEmbedded is false on this
            path, so its kind is 'empty' or 'standalone', and `setActiveTab`
            in standalone mode calls setParams with the same shape this used
            to build inline — see the `setActiveTab` definition above. */}
        {rightPane}
      </Content>
      <ResetVaultDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onAfterReset={refresh}
      />
    </Layout>
  );
}

function EmptyRight() {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Empty description={
        <Space orientation="vertical" align="center">
          <Text>Select a table on the left to view its details.</Text>
          <Text type="secondary">Browse columns, preview rows, and check sync history.</Text>
        </Space>
      } />
    </div>
  );
}

// ── Embedded right pane: merged Details + Columns ────────────────────
// Used inside the Data Center Catalog tab. Sync History lives in its
// own top-level Data Center tab; row counts are intentionally omitted
// (they'd require a full ERD fetch — see embeddedTableSchema flow).
function TableOverviewPanel({ table }) {
  const cols = table.columns || [];
  const pkCols = cols.filter((c) => c.is_primary_key);
  const fkCols = cols.filter((c) => c.is_foreign_key);
  const data = cols.map((c, i) => ({ key: i, ...c }));

  // Total records is loaded on demand (potentially expensive on large
  // tables). Clear cached count whenever the user switches tables.
  const [totalRecords, setTotalRecords] = useState(null);
  const [totalLoading, setTotalLoading] = useState(false);
  const [totalError, setTotalError] = useState(null);
  useEffect(() => {
    setTotalRecords(null);
    setTotalError(null);
  }, [table.schema_name, table.table]);
  const loadTotal = useCallback(async () => {
    if (!table.schema_name || !table.table) {return;}
    setTotalLoading(true);
    setTotalError(null);
    try {
      const r = await getVaultData(table.schema_name, table.table, 1, 0);
      setTotalRecords(r.total ?? 0);
    } catch (err) {
      setTotalError(err.message || String(err));
    } finally {
      setTotalLoading(false);
    }
  }, [table.schema_name, table.table]);
  const columns = [
    {
      title: '#', dataIndex: 'key', width: 50,
      render: (v) => <Text type="secondary">{v + 1}</Text>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (v, r) => (
        <Space size={6}>
          <code>{v}</code>
          {r.is_primary_key && <Tooltip title="Primary key"><KeyOutlined style={{ color: '#722ed1' }} /></Tooltip>}
          {r.is_foreign_key && <Tooltip title={`FK → ${r.fk_target_table}.${r.fk_target_column}`}><LinkOutlined style={{ color: '#52c41a' }} /></Tooltip>}
        </Space>
      ),
    },
    { title: 'Type', dataIndex: 'type', render: (v) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text> },
    {
      title: 'Constraints',
      render: (_, r) => (
        <Space size={4}>
          {r.is_primary_key && <Tag color="purple">PK</Tag>}
          {r.is_foreign_key && (
            <Tag color="green">FK → {r.fk_target_table}.{r.fk_target_column}</Tag>
          )}
          {r.is_cursor && <Tag color="orange">cursor</Tag>}
        </Space>
      ),
    },
  ];
  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: JUSTIFY_SPACE_BETWEEN }} align="center">
        <Space orientation="vertical" size={0}>
          <Space size={6}>
            <Tag color="purple">{table.connector}</Tag>
            <ApartmentOutlined />
            <Title level={4} style={{ margin: 0 }}>{table.table}</Title>
          </Space>
          <Text type="secondary">{cols.length} columns</Text>
        </Space>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[24, 16]}>
          <Col span={6}><Statistic title="Columns" value={cols.length} /></Col>
          <Col span={6}><Statistic title="Primary key" value={pkCols.length} /></Col>
          <Col span={6}><Statistic title="Foreign keys" value={fkCols.length} /></Col>
          <Col span={6}>
            <Statistic
              title={
                <Space size={6}>
                  <span>Total records</span>
                  <Tooltip title={totalRecords != null ? 'Refresh count' : 'Run count'}>
                    <Button
                      size="small"
                      type={totalRecords == null ? 'primary' : 'default'}
                      icon={totalRecords == null ? <PlayCircleOutlined /> : <ReloadOutlined />}
                      onClick={loadTotal}
                      loading={totalLoading}
                    >
                      {totalRecords == null ? 'Run' : ''}
                    </Button>
                  </Tooltip>
                </Space>
              }
              value={totalError ? 'error' : (totalRecords ?? '—')}
              styles={{ content: totalError ? { color: '#cf1322', fontSize: 16 } : undefined }}
            />
            {totalError && (
              <Text type="danger" style={{ fontSize: 12 }}>{totalError}</Text>
            )}
          </Col>
        </Row>
        <div style={{ marginTop: 24 }}>
          <Title level={5}>Identity</Title>
          <Paragraph style={{ marginBottom: 4 }}>
            <Text type="secondary">Schema:</Text> <code>{table.schema_name}</code>
          </Paragraph>
          <Paragraph style={{ marginBottom: 4 }}>
            <Text type="secondary">Table:</Text> <code>{table.table}</code>
          </Paragraph>
          <Paragraph style={{ marginBottom: 4 }}>
            <Text type="secondary">Source connector:</Text> <Tag color="blue">{table.connector}</Tag>
          </Paragraph>
        </div>
      </Card>

      <Card title={<Space><FieldNumberOutlined /> Columns</Space>}>
        <Table size="small" pagination={false} dataSource={data} columns={columns} />
      </Card>
    </div>
  );
}

// ── Right pane: tabs ─────────────────────────────────────────────────
function TableDetailPanes({ table, connections, activeTab, onTab }) {
  // Resolve which connection feeds this schema (1:1 with connector_name).
  const connection = useMemo(
    () => connections.find((c) => c.connector_name === table.connector) || null,
    [connections, table.connector],
  );

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: JUSTIFY_SPACE_BETWEEN }} align="center">
        <Space orientation="vertical" size={0}>
          <Space size={6}>
            <Tag color="purple">{table.connector}</Tag>
            <ApartmentOutlined />
            <Title level={4} style={{ margin: 0 }}>{table.table}</Title>
          </Space>
          <Text type="secondary">
            {(table.row_count || 0).toLocaleString()} rows · {table.columns.length} columns
          </Text>
        </Space>
      </Space>

      <Tabs
        activeKey={activeTab}
        onChange={onTab}
        items={[
          {
            key: 'details',
            label: <span><DatabaseOutlined /> Table Details</span>,
            children: <DetailsTab table={table} connection={connection} />,
          },
          {
            key: 'columns',
            label: <span><FieldNumberOutlined /> Columns</span>,
            children: <ColumnsTab table={table} />,
          },
          {
            key: 'history',
            label: <span><ReloadOutlined /> Sync History</span>,
            children: <HistoryTab table={table} connection={connection} />,
          },
        ]}
      />
    </div>
  );
}

function DetailsTab({ table, connection }) {
  const pkCols = table.columns.filter((c) => c.is_primary_key);
  const fkCols = table.columns.filter((c) => c.is_foreign_key);
  const cursorCol = table.columns.find((c) => c.is_cursor);

  return (
    <Card>
      <Row gutter={[24, 16]}>
        <Col span={6}><Statistic title="Rows" value={table.row_count || 0} /></Col>
        <Col span={6}><Statistic title="Columns" value={table.columns.length} /></Col>
        <Col span={6}><Statistic title="Primary key" value={pkCols.length} /></Col>
        <Col span={6}><Statistic title="Foreign keys" value={fkCols.length} /></Col>
      </Row>
      <div style={{ marginTop: 24 }}>
        <Title level={5}>Identity</Title>
        <Paragraph style={{ marginBottom: 4 }}>
          <Text type="secondary">Schema:</Text> <code>{table.schema_name}</code>
        </Paragraph>
        <Paragraph style={{ marginBottom: 4 }}>
          <Text type="secondary">Table:</Text> <code>{table.table}</code>
        </Paragraph>
        <Paragraph style={{ marginBottom: 4 }}>
          <Text type="secondary">Source connector:</Text> <Tag color="blue">{table.connector}</Tag>
        </Paragraph>
        {connection ? (
          <Paragraph style={{ marginBottom: 4 }}>
            <Text type="secondary">Connection:</Text> <code>{connection.name}</code>
            {' '}<Tag>{connection.id}</Tag>
          </Paragraph>
        ) : (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
            title="No active connection found for this schema. The table is in the vault but its source connection has been removed."
          />
        )}
        {pkCols.length > 0 && (
          <Paragraph style={{ marginBottom: 4 }}>
            <Text type="secondary">Primary key:</Text>{' '}
            {pkCols.map((c) => <code key={c.name} style={{ marginRight: 6 }}>{c.name}</code>)}
          </Paragraph>
        )}
        {cursorCol && (
          <Paragraph style={{ marginBottom: 4 }}>
            <Text type="secondary">Incremental cursor:</Text>{' '}
            <code>{cursorCol.name}</code> <Tag color="orange">cursor</Tag>
          </Paragraph>
        )}
      </div>
    </Card>
  );
}

function ColumnsTab({ table }) {
  const data = table.columns.map((c, i) => ({ key: i, ...c }));
  const columns = [
    {
      title: '#', dataIndex: 'key', width: 50,
      render: (v) => <Text type="secondary">{v + 1}</Text>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (v, r) => (
        <Space size={6}>
          <code>{v}</code>
          {r.is_primary_key && <Tooltip title="Primary key"><KeyOutlined style={{ color: '#722ed1' }} /></Tooltip>}
          {r.is_foreign_key && <Tooltip title={`FK → ${r.fk_target_table}.${r.fk_target_column}`}><LinkOutlined style={{ color: '#52c41a' }} /></Tooltip>}
        </Space>
      ),
    },
    { title: 'Type', dataIndex: 'type', render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Constraints',
      render: (_, r) => (
        <Space size={4}>
          {r.is_primary_key && <Tag color="purple">PK</Tag>}
          {r.is_foreign_key && (
            <Tag color="green">FK → {r.fk_target_table}.{r.fk_target_column}</Tag>
          )}
          {r.is_cursor && <Tag color="orange">cursor</Tag>}
        </Space>
      ),
    },
  ];
  return (
    <Card>
      <Table size="small" pagination={false} dataSource={data} columns={columns} />
    </Card>
  );
}

// History comes from Dagster, the same way DATA CENTER's Sync History tab
// reads it (see services/syncHistory.js). This tab used to call
// `GET /syncs/streams/history`, which reads the `sync_stream_runs` audit
// table — unwritten since the Dagster cutover, so the tab was always empty.
// The old on-error fallback re-derived rows from `run.streams`, a field the
// run-LIST adapter never fills, so it was empty too.
function HistoryTab({ table, connection }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!connection) { setHistory([]); return; }
    setLoading(true);
    try {
      const runs = await listDagitRuns({ connectionId: connection.id, limit: 50 })
        .catch(() => []);
      const { stream, prefix } = resolveStreamAndPrefix(
        table.table, connection.connector_name,
      );
      const relevant = filterSyncRunsForStream(runs, stream, prefix);
      // Per-run detail for the materialization rowcount + timing. One request
      // per relevant run, capped at 30 by `filterSyncRunsForStream`.
      const details = await Promise.all(
        relevant.map((r) => getDagitRun(r.id).catch(() => null)),
      );
      setHistory(buildSyncHistoryRows(relevant, details, stream, prefix));
    } catch (err) {
      message.warning(`Couldn't load sync history: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [connection, table]);

  useEffect(() => { load(); }, [load]);

  if (!connection) {
    return (
      <Alert
        type="info"
        showIcon
        title="Sync history isn't available — the source connection for this schema has been removed."
      />
    );
  }

  const columns = [
    {
      title: 'Run', dataIndex: 'run_id', width: 200,
      render: (v) => <code style={{ fontSize: 11 }}>{v}</code>,
    },
    {
      // Dagster statuses (success/failure/started/canceled/...), not the
      // legacy succeeded/failed/running set the old endpoint returned.
      title: 'Status', dataIndex: 'status', width: 120,
      render: (v) => <Tag color={syncHistoryStatusColor(v)}>{v || '—'}</Tag>,
    },
    {
      title: 'Rows', dataIndex: 'rows_loaded', width: 100, align: 'right',
      render: (v) => v != null ? v.toLocaleString() : '—',
    },
    {
      title: 'Started', dataIndex: 'started_at', width: 170,
      render: (v) => v ? new Date(v).toLocaleString() : '—',
    },
    {
      title: 'Finished', dataIndex: 'finished_at', width: 170,
      render: (v) => v ? new Date(v).toLocaleString() : '—',
    },
    {
      title: 'Error', dataIndex: 'error_message',
      render: (v) => v ? <Text type="danger" ellipsis style={{ maxWidth: 320 }}>{v}</Text> : '—',
    },
  ];

  let body;
  if (loading) {
    body = <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
  } else if (history.length === 0) {
    body = <Empty description="No sync runs recorded for this table yet." />;
  } else {
    body = (
      <Table
        size="small"
        rowKey={(r, i) => r.run_id ? `${r.run_id}-${i}` : i}
        dataSource={history}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: false }}
      />
    );
  }

  return (
    <Card
      title={<Space><ReloadOutlined /> Sync history for <code>{table.table}</code></Space>}
      extra={<Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>}
    >
      {body}
    </Card>
  );
}
